import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import License from '../../models/License';
import RenderUser from '../RenderUser';

interface LicenseItemProps {
    license: License;
    openModal: (license: License) => void;
};

type LicenseStatus = 'in-use' | 'transferred' | 'available';

const _getStatus = (license: License): LicenseStatus => {
    if (license.resources) return 'in-use';
    if (license.transferred_user_name) return 'transferred';
    return 'available';
};

const LicenseItem: React.FC<LicenseItemProps> = ({ license, openModal }) => {

    const [t] = useTranslation();

    const status = _getStatus(license);

    const badgeMap: Record<LicenseStatus, { modifier: string; label: string }> = {
        'in-use':      { modifier: 'configured',  label: t('In_use') },
        'transferred': { modifier: 'transferred', label: t('Transferred') },
        'available':   { modifier: 'online',      label: t('Available') },
    };

    const badge = badgeMap[status];

    const _renderMiddle = () => {
        if (license.resources) {
            const screen = license.resources as any;
            return (
                <div className="screen-item__playing">
                    <div className="screen-item__playing__title">{ t('Associated_device') }</div>
                    <Link
                        to={`screens/${screen.id}`}
                        className="license-item__device-link"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <i className="iconsminds-monitor license-item__device-icon"></i>
                        <span>{ screen.name ?? '—' }</span>
                    </Link>
                </div>
            );
        }
        if (license.user_transfer) {
            const name = `${license.user_transfer?.name ?? ''} ${(license.user_transfer as any)?.last_name ?? ''}`.trim()
                || license.transferred_user_name
                || '—';
            return (
                <div className="screen-item__playing">
                    <div className="screen-item__playing__title">{ t('Transferred_to') }</div>
                    <span className="license-item__transfer-chip">
                        <i className="iconsminds-male-female license-item__device-icon"></i>
                        <span>{ name }</span>
                    </span>
                </div>
            );
        }
        return <div className="screen-item__playing" />;
    };

    return (
        <div className="card mb-3">
            <div className="card-body screen-item">

                {/* License ID + status badge */}
                <div className="screen-item__info">
                    <Link to={`licenses/${license.id}`} className="screen-item__name">
                        <i className="iconsminds-diploma-2" style={{ flexShrink: 0 }}></i>
                        <span className="ml-2 license-item__id">{ license.name ?? license.id }</span>
                    </Link>
                    <div className="screen-item__tags">
                        <span className={`screen-item__status-badge screen-item__status-badge--${badge.modifier}`}>
                            <span className="screen-item__status-dot" />
                            <span className="screen-item__status-label">{ badge.label }</span>
                        </span>
                    </div>
                </div>

                {/* Middle: device or transfer info */}
                { _renderMiddle() }

                {/* Actions */}
                <div className="screen-item__actions">
                    <RenderUser item={license} />
                    <Link to={`licenses/${license.id}`} className="screen-item__detail-link">
                        <FontAwesomeIcon icon={faChevronRight} />
                    </Link>
                </div>

            </div>
        </div>
    );

};

export default React.memo(LicenseItem);
